export const en = {
  nav: {
    services: "Services",
    solutions: "Solutions",
    industries: "Industries",
    work: "Work",
    about: "About",
    insights: "Insights",
    contact: "Contact",
    talkToUs: "Discuss a costly workflow",
    seeWhatWeBuild: "Review solution patterns",
    servicesOverview: "Production AI services",
    industriesOverview: "Industry fit",
    primaryAria: "Primary navigation",
    mobileAria: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  offerCard: {
    learnMore: "Open the service brief",
  },
  home: {
    ctaHeadline: "Bring the costly workflow your team should not have to keep carrying.",
    ctaDescription:
      "A fit review tests the cost, workflow boundary, data, ownership, risk, and evidence before recommending a build, readiness work, a purchased tool, or no build.",
  },
  about: {
    heroLabel: "About Vert AI",
    heroTitle: "Senior accountability from the first workflow decision through production.",
    heroSubtitle:
      "Vert AI is a Canada-based firm that fixes costly commerce workflows by combining operating design, production AI, reliable data, controls, adoption, and measurement.",
    secondaryCta: "Review engagement paths",
    heroProof: [
      {
        label: "Commerce operations first",
        desc: "The primary focus is mid-market retail and ecommerce, consumer goods, and franchise or multi-location operations in Canada.",
      },
      {
        label: "Founder-led delivery",
        desc: "Guillaume leads scoping and key technical decisions; vetted specialists contribute where the scope requires them.",
      },
      {
        label: "Phased and evidence-led",
        desc: "Projects move through bounded decisions and expand only after the workflow, controls, adoption, and value can be inspected.",
      },
    ],
    whyExistsLabel: "Why Vert exists",
    whyExistsTitle: "The work is not complete until the operating change holds.",
    whyExistsP1:
      "Companies need better throughput and decisions, reliable data, clear human authority, and systems that remain observable after launch.",
    whyExistsP2:
      "Vert AI exists for operators who need the workflow redesign and production discipline around AI: select the right process, establish the baseline, prepare the data, integrate the system, evaluate failures, define human review, and monitor adoption and value. Not every workflow should be automated.",
    principlesLabel: "Principles",
    principlesTitle: "How Vert AI decides what should be built.",
    principles: [
      {
        title: "Start with expensive workflows, not technology demos.",
        desc: "The first question is where speed, quality, cost, or decision accuracy is already creating measurable pressure.",
      },
      {
        title: "Define the baseline before building.",
        desc: "Cycle time, exception rate, review effort, error cost, adoption, and decision quality need a starting point.",
      },
      {
        title: "Make data readiness visible.",
        desc: "Source quality, ownership, permissions, semantics, and missing instrumentation are surfaced before they become production risk.",
      },
      {
        title: "Keep humans in control where risk, judgment, or customer impact matters.",
        desc: "AI should route, recommend, draft, retrieve, and score with clear review paths where accountability, privacy, or customer experience matters.",
      },
      {
        title: "Design for production from day one.",
        desc: "Evaluation, observability, fallback logic, access control, and operational ownership are part of the system design.",
      },
      {
        title: "Measure adoption and operating impact.",
        desc: "A system is not successful because it launched. It is successful when people use it and the workflow improves.",
      },
      {
        title: "Expand only after trust is earned.",
        desc: "The next workflow should come after the first one proves reliability, adoption, and business usefulness.",
      },
    ],
    differenceLabel: "What makes the work different",
    differenceTitle: "Strategy, data, workflow, and production engineering stay connected.",
    differenceItems: [
      {
        title: "Strategy and build in one team.",
        desc: "The people shaping the recommendation stay close to architecture, implementation, and launch constraints.",
      },
      {
        title: "AI + data + workflow + decision logic.",
        desc: "Useful AI usually depends on clean data paths, explicit rules, usable interfaces, and escalation design.",
      },
      {
        title: "Production controls included.",
        desc: "Evaluation, monitoring, auditability, human review, and fallback behavior are treated as core scope.",
      },
      {
        title: "Clear ownership and governance.",
        desc: "Every system needs accountable owners for data, logic, review, performance, and change management.",
      },
    ],
    trustLabel: "Trust and operating discipline",
    trustTitle: "Trust is designed into the system, not added at launch.",
    trustIntro:
      "Production AI has to be inspectable. Vert AI treats evaluation, monitoring, escalation, data quality, privacy, security review, ownership, adoption, and ROI measurement as implementation requirements, not slideware.",
    trustItems: [
      {
        title: "Evaluation",
        desc: "Test sets, acceptance criteria, edge cases, and review loops are defined so the system can be judged honestly.",
      },
      {
        title: "Monitoring",
        desc: "Usage, quality, latency, drift, failure modes, and business metrics need visibility after launch.",
      },
      {
        title: "Escalation logic",
        desc: "Confidence thresholds, exception routing, and human review paths keep judgment where it belongs.",
      },
      {
        title: "Data quality",
        desc: "Inputs, transformations, definitions, and ownership are made explicit so the system does not inherit hidden ambiguity.",
      },
      {
        title: "Privacy and security",
        desc: "Access, permissions, sensitive data handling, vendor exposure, and security review are considered before workflows are automated.",
      },
      {
        title: "Governance",
        desc: "Change control, documentation, ownership, and review cadence keep the system usable as the business changes.",
      },
    ],
    founderLabel: "Founder",
    founderName: "Guillaume Belisle",
    founderRole: "Founder, Vert AI",
    founderTitle: "Founder-led where judgment matters; specialist-supported where depth is required.",
    founderP1:
      "Guillaume Belisle is a data scientist, ML engineer, and AI systems builder. His prior professional experience includes designing and shipping production AI, ML, and data systems in environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware. Those companies are not represented as Vert AI clients.",
    founderP2:
      "Guillaume leads opportunity selection, scoping, architecture, and key technical decisions. Vetted specialists may support engineering, data, design, security, or domain work when the approved scope requires them.",
    founderP3:
      "The operating bias is commercial and technical at the same time. If a system cannot be measured, governed, adopted, and used with confidence by the people relying on it, it is not ready.",
    founderP4:
      "Vert AI is not the right partner for an unowned demo, a predetermined tool rollout with no operating case, or a guarantee of outcomes that cannot be measured and attributed responsibly.",
    ctaHeadline: "Bring the costly workflow that needs to perform differently.",
    ctaDesc:
      "A fit review will test the operating value, data reality, owner, risk, and most credible engagement path—including a no-build recommendation when appropriate.",
  },
  insights: {
    heroLabel: "Insights",
    heroTitle: "Make the next AI decision with evidence, not momentum.",
    heroSubtitle: "Sixteen practical guides for operators, data leaders, finance, IT, security, and executives evaluating workflow value, readiness, controls, economics, and production risk.",
    featured: "Featured",
    readArticle: "Read the operating guide",
    articles: [
      {
        slug: "production-grade-ai",
        cat: "Production AI",
        title: "Production-grade AI: the operating standard beyond a demo",
        desc: "A production AI system must remain useful under real data, workload, and risk conditions. This guide defines the workflow, evaluation, controls, monitoring, and ownership required beyond a successful demo.",
        seoTitle: "Production-Grade AI Requirements | Vert AI",
        seoDescription: "Learn what production-grade AI requires across workflow design, evaluation, controls, monitoring, human review, ownership, and operational resilience.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "production-grade AI, production AI system, AI deployment, AI evaluation, AI monitoring, AI controls",
        tags: ["Production AI", "AI Systems", "Evaluation", "Monitoring", "Governance"],
      },
      {
        slug: "workflows-deserve-ai",
        cat: "Workflow Selection",
        title: "Which workflows deserve AI? A production selection framework",
        desc: "A practical framework for choosing AI workflows by operating value, frequency, bounded judgment, data readiness, risk, and the organization's ability to act on the output.",
        seoTitle: "How to Select AI Workflows for Production | Vert AI",
        seoDescription: "Use a practical framework to rank AI workflows by operating value, frequency, data readiness, bounded judgment, risk, and ability to act on outputs.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI workflow selection, choose AI use case, AI automation framework, AI readiness, first AI workflow",
        tags: ["Workflow Selection", "AI Readiness", "Automation", "Operating Value", "Production AI"],
      },
      {
        slug: "vet-ai-vendor",
        cat: "Buyer Guide",
        title: "How to vet an AI vendor: evidence, controls, data rights, and exit terms",
        desc: "Evaluate an AI vendor against one production case. Test representative evidence, architecture, permissions, operating responsibility, total cost, data rights, portability, and practical exit terms.",
        seoTitle: "AI Vendor Evaluation Checklist | Vert AI",
        seoDescription: "Evaluate AI vendors against a production case, including evidence quality, architecture, permissions, total cost, data rights, portability, and exit terms.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "vet AI vendor, AI vendor evaluation, AI procurement, AI contract data rights, AI vendor portability, AI due diligence",
        tags: ["AI Vendors", "Procurement", "Due Diligence", "Data Rights", "Production AI"],
      },
      {
        slug: "ai-pilots-fail-before-production",
        cat: "Production AI",
        title: "Why AI pilots stall before production",
        desc: "AI pilots stall when a capability is tested without the live data path, workflow contract, controls, exception design, accountable owner, and unit economics required for production.",
        seoTitle: "Why AI Pilots Stall Before Production | Vert AI",
        seoDescription: "See why AI pilots stall when live data paths, workflow ownership, controls, exception handling, integration, and credible unit economics are missing.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI pilots fail, production AI, AI proof of concept, AI deployment readiness, AI workflow ownership, AI pilot ROI",
        tags: ["Production AI", "AI Pilots", "Workflow Design", "Data Readiness", "AI Economics"],
      },
      {
        slug: "production-ai-readiness-checklist-mid-market",
        cat: "AI Readiness",
        title: "Production AI readiness checklist for mid-market operators",
        desc: "A workflow-level readiness checklist covering operating value, process clarity, data fitness, ownership, risk, human review, integration, total cost, and baseline measurement.",
        seoTitle: "Production AI Readiness Checklist | Vert AI",
        seoDescription: "Assess one workflow for production AI across business value, process clarity, data fitness, ownership, human review, integration, risk, and total cost.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI readiness checklist, mid-market AI, production AI readiness, workflow assessment, AI data readiness, AI scorecard",
        tags: ["AI Readiness", "Mid-market", "Workflow Assessment", "Data Readiness", "Production AI"],
      },
      {
        slug: "decision-intelligence-vs-business-intelligence",
        cat: "Decision Systems",
        title: "Decision intelligence vs business intelligence: from visibility to action",
        desc: "Business intelligence creates trusted visibility. Decision intelligence adds models, rules, constraints, action paths, and outcome feedback when repeated decisions remain slow or inconsistent.",
        seoTitle: "Decision Intelligence vs Business Intelligence | Vert AI",
        seoDescription: "Compare business intelligence and decision intelligence; see when governed models, rules, action paths, and outcome feedback improve repeated decisions.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "decision intelligence vs business intelligence, BI vs decision systems, decision engine, operational analytics, AI decision systems",
        tags: ["Decision Intelligence", "Business Intelligence", "Decision Systems", "Analytics", "Workflow"],
      },
      {
        slug: "ai-governance-monitoring-evaluation-human-review",
        cat: "AI Governance",
        title: "AI governance for operators: evaluation, monitoring, and human review",
        desc: "Turn AI governance into operating practice with an inventory, risk tiers, decision rights, acceptance records, production monitoring, human-review design, incident response, and retirement controls.",
        seoTitle: "AI Governance, Monitoring and Human Review | Vert AI",
        seoDescription: "Make AI governance practical with risk tiers, decision rights, acceptance evidence, monitoring, human review, incident response, and retirement controls.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI governance for operators, AI monitoring, AI evaluation, human review AI, AI incident response, production AI governance",
        tags: ["AI Governance", "Monitoring", "Evaluation", "Human Review", "Production Controls"],
      },
      {
        slug: "what-agentic-ai-should-automate",
        cat: "Agentic AI",
        title: "What agentic AI should and should not automate",
        desc: "Use agentic AI for defined, multi-step, verifiable work with bounded tools and authority. Keep ambiguous, consequential, or hard-to-reverse decisions assisted or human-led.",
        seoTitle: "What Should Agentic AI Automate? | Vert AI",
        seoDescription: "Identify multi-step workflows suited to bounded agentic AI, decisions that need human control, and the tools, limits, evidence, and escalation required.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "agentic AI automation, AI agents workflow, what AI agents should automate, bounded autonomy, human review agents",
        tags: ["Agentic AI", "AI Agents", "Workflow Automation", "Bounded Autonomy", "Human Review"],
      },
      {
        slug: "data-foundation-behind-useful-ai-systems",
        cat: "Data Platforms",
        title: "The data foundation behind useful AI systems",
        desc: "Production AI needs use-case-specific data contracts, resolved identities, governed semantics, quality controls, permissions, freshness, provenance, observability, and accountable source owners.",
        seoTitle: "Data Foundations for Production AI | Vert AI",
        seoDescription: "Build AI data foundations with contracts, governed semantics, resolved identities, quality controls, permissions, provenance, and accountable owners.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI data foundation, AI-ready data, data platforms for AI, semantic layer, data quality for AI, production AI data",
        tags: ["Data Platforms", "AI-ready Data", "Semantic Layer", "Data Quality", "Production AI"],
      },
      {
        slug: "measure-ai-roi-before-building",
        cat: "AI Economics",
        title: "How to measure AI ROI before building",
        desc: "Frame AI ROI with a workflow baseline, value tree, full operating cost, credible counterfactual, unit economics, scenario analysis, and evidence gates before development begins.",
        seoTitle: "How to Measure AI ROI Before Building | Vert AI",
        seoDescription: "Estimate AI ROI before development with workflow baselines, full operating cost, credible counterfactuals, unit economics, scenarios, and evidence gates.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI ROI measurement, AI business case, AI baseline metrics, AI unit economics, AI total cost, measure AI value",
        tags: ["AI ROI", "Business Case", "Unit Economics", "Measurement", "Investment Gates"],
      },
      {
        slug: "dashboard-to-decision-engine-production",
        cat: "Decision Systems",
        title: "From dashboard to decision engine: what changes in production",
        desc: "A decision engine connects governed inputs, analytical logic, business policy, an action path, and outcome feedback. Learn when to move beyond BI and how to stage the transition safely.",
        seoTitle: "Dashboard to Decision Engine: Production Guide | Vert AI",
        seoDescription: "Learn when dashboards should become decision engines and how governed inputs, analytical logic, policy, action paths, and outcome feedback connect safely.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "dashboard to decision engine, decision engine, decision intelligence, production decision systems, operational analytics",
        tags: ["Decision Engines", "Decision Intelligence", "Business Intelligence", "Workflow", "Production AI"],
      },
      {
        slug: "human-in-the-loop-economics-2026",
        cat: "AI Operations",
        title: "Human-in-the-loop economics: design review as an operating system",
        desc: "Human review is both a control and an operating cost. Design the decision, evidence, authority, queue, staffing, thresholds, and measurement around consequence, uncertainty, and reversibility.",
        seoTitle: "Human-in-the-Loop AI Economics | Vert AI",
        seoDescription: "Design human review as a production control and operating cost, with clear authority, queues, staffing, thresholds, escalation, and performance measures.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "human in the loop AI, AI review workflow, human review cost, HITL economics, AI confidence threshold, AI escalation queue",
        tags: ["AI Operations", "Human Review", "AI Economics", "Workflow Design", "Production AI"],
      },
      {
        slug: "evaluation-gap-honest-ai-scorecard",
        cat: "AI Evaluation",
        title: "The AI evaluation gap: build a decision-grade scorecard",
        desc: "Close the gap between technical scores and operating proof with offline, shadow, controlled-outcome, and production evaluation that exposes failures, uncertainty, cost, and business results.",
        seoTitle: "AI Evaluation Scorecard for Production | Vert AI",
        seoDescription: "Build an AI evaluation scorecard connecting offline, shadow, controlled-outcome, and production tests to failures, uncertainty, cost, and business results.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI evaluation framework, enterprise AI metrics, model monitoring, AI scorecard, shadow evaluation, production AI evaluation",
        tags: ["AI Evaluation", "AI Scorecard", "Model Monitoring", "AI Operations", "Production AI"],
      },
      {
        slug: "build-vs-buy-ai-2026-decision-framework",
        cat: "AI Strategy",
        title: "Build vs buy AI in 2026: a production decision framework",
        desc: "Choose build, buy, or compose boundaries by differentiation, workflow fit, data and control, integration, total economics, operating capacity, third-party risk, and reversibility.",
        seoTitle: "Build vs Buy AI: Production Decision Guide | Vert AI",
        seoDescription: "Choose where to build, buy, or compose AI by workflow fit, differentiation, data control, integration, cost, operating capacity, risk, and reversibility.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "build vs buy AI, AI decision framework, AI architecture, AI vendor evaluation, AI total cost, AI portability",
        tags: ["AI Strategy", "Build vs Buy", "AI Architecture", "Vendor Selection", "Production AI"],
      },
      {
        slug: "semantic-layer-undervalued-asset",
        cat: "Data Platforms",
        title: "The semantic layer: governed meaning for analytics and AI",
        desc: "A semantic layer makes business entities, metrics, states, rules, and context reusable across analytics and AI. Learn what it contains, where to start, and how to operate it as a product.",
        seoTitle: "Semantic Layers for Analytics and AI | Vert AI",
        seoDescription: "Define a semantic layer that makes business entities, metrics, states, rules, and context reusable across analytics, AI systems, and governed decisions.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "semantic layer, governed metrics, AI semantic layer, business definitions, data contracts, analytics governance",
        tags: ["Semantic Layer", "Data Platforms", "Analytics Governance", "AI-ready Data", "Decision Systems"],
      },
      {
        slug: "agentic-ai-production-failures",
        cat: "Agentic AI",
        title: "Agentic AI production failures: goals, tools, state, and control",
        desc: "Agentic systems fail across goals, tool permissions, multi-step trajectories, state, concurrency, evaluation, human handoffs, and cost. Build the control plane before expanding autonomy.",
        seoTitle: "Agentic AI Failure Modes in Production | Vert AI",
        seoDescription: "Examine agentic AI failures across goals, tool permissions, state, concurrency, evaluation, handoffs, and cost; design controls before autonomy expands.",
        date: "July 2026",
        readTime: "3 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "agentic AI production failures, AI agent security, AI agent evaluation, tool permissions, agent state, agentic AI controls",
        tags: ["Agentic AI", "AI Agents", "Production Failures", "Security", "Control Plane"],
      },
    ],
  },
  contact: {
    heroLabel: "Discuss a costly workflow",
    heroTitle: "Bring one costly workflow or recurring decision.",
    heroSubtitle:
      "We will test its operating cost, boundary, data, owner, risk, and evidence—then recommend the most credible next step, including no build when that is the right answer.",
    bookingLabel: "Fit conversation",
    bookingTitle: "Book a workflow fit call",
    bookingDesc:
      "This founder-led conversation tests the operating value, workflow boundary, data reality, owner, risks, and whether there is a credible first production scope. It is not a generic AI discovery call.",
    bookingMeta: "Founder-led · workflow first · no-build answer when appropriate",
    fallbackBooking: "Send workflow context",
    howToReach: "Before you send",
    scopingCall: "Enough context for a useful response.",
    scopingCallDesc:
      "Bring the current process, the recurring exception or decision, the people involved, and the operating signal that should improve. Do not send confidential customer data through this form.",
    emailLabel: "Email",
    locationLabel: "Location",
    location: "Montreal, Canada",
    responseTimeLabel: "Who leads scoping",
    responseTime: "Guillaume Belisle, founder",
    formLabel: "Workflow context",
    formTitle: "Describe the workflow or decision that is costing the business.",
    formIntro:
      "Name the work, who owns it, and where the cost appears. Only your name, work email, company, and workflow context are required.",
    formName: "Name",
    formNamePlaceholder: "Your name",
    formCompany: "Company",
    formCompanyPlaceholder: "Company name",
    formEmail: "Work email",
    formEmailPlaceholder: "you@company.com",
    formMessage: "Workflow or decision to improve",
    formMessagePlaceholder:
      "What happens today? Where do delay, manual work, inconsistency, error, or capacity constraints show up? What should improve?",
    formSubmit: "Send for fit review",
    formSending: "Sending for review...",
    formSuccessTitle: "Workflow context received.",
    formSuccessDesc:
      "Vert AI will review it and respond with a fit-call path, a clarifying question, or an honest no-build recommendation.",
    formErrorTitle: "The inquiry did not send.",
    formErrorDesc:
      "Try again or email the same workflow context directly.",
    validationRequired: "Add this detail before sending.",
    validationEmail: "Enter a valid work email.",
    reassuranceLabel: "What happens next",
    reassuranceTitle: "A fit review designed to protect both sides’ time.",
    reassurance: [
      {
        title: "Founder-led first conversation",
        desc: "Vert AI reviews the context. Guillaume leads scoping and the key technical decisions if the work proceeds.",
      },
      {
        title: "Workflow before technology",
        desc: "The review starts with the current process, owner, exceptions, data, economics, and change required—not a preferred model or tool.",
      },
      {
        title: "Clear next steps",
        desc: "If there is a fit, the next step identifies the appropriate engagement path, required evidence, key risks, and the decision needed to proceed.",
      },
      {
        title: "Practical recommendation",
        desc: "If the best answer is not to build yet, Vert AI will say that and identify what should be resolved first.",
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "Common questions before a fit call.",
    faqs: [
      {
        q: "What happens after I submit?",
        a: "Vert AI reviews the workflow, data, timing, and fit signals. Guillaume leads scoping if the work appears suitable. The response is a fit-call path, a clarifying question, or a recommendation not to build yet.",
      },
      {
        q: "Do we need a defined use case?",
        a: "No. A rough workflow, recurring decision, data bottleneck, or operational pain is enough to start. Vert AI can help determine whether it should become an AI, data, automation, or decision-intelligence project.",
      },
      {
        q: "Can you work with our existing stack?",
        a: "Usually, yes. The first step is understanding current systems, data access, integration constraints, security and privacy requirements, and who will own the workflow after launch.",
      },
      {
        q: "Do you sign NDAs?",
        a: "Confidentiality requirements can be discussed before sensitive technical or operating detail is shared. Do not submit confidential customer data through the website form.",
      },
      {
        q: "Do you work with teams outside Canada?",
        a: "Yes. Vert AI is based in Canada and serves Canadian companies first, with selective delivery for US teams.",
      },
    ],
  },
  notFound: {
    title: "Page not found",
    message: "This address does not lead to an active Vert AI page. Return to the homepage to choose an engagement, review real work, or assess a workflow.",
    backHome: "Return to the homepage",
  },
  footer: {
    tagline: "Fixing costly commerce workflows with production AI, reliable data, and measurable operating controls. Canada first.",
    engagements: "Engagements",
    services: "Services",
    industries: "Industries",
    insights: "Insights",
    company: "Company",
    contactLabel: "Contact",
    copyright: "Vert AI Inc.",
    bottomTagline: "AI, Data & Decision Systems · Canada",
  },
  solutionsHub: {
    heroLabel: "Solutions",
    heroTitle: "Five ways to fix costly work without losing control.",
    heroSubtitle: "Start with the operating failure—manual handoffs, document friction, scattered knowledge, service queues, or slow decisions—then see the data, authority, controls, and engagement path each solution requires.",
    primaryLabel: "Solution categories",
    primaryTitle: "Review by operating problem.",
    primarySubtitle: "Each category maps a recurring operating problem to the system pattern, controls, data needs, and service path that fit.",
    secondaryLabel: "Additional capabilities",
    secondaryTitle: "Supporting domains.",
    recommendationTitle: "Recommendation and personalization",
    recommendationDesc: "Recommendation engines and next-best-action logic for ecommerce, merchandising, and customer experience.",
    recommendationVia: "Via Decision Intelligence",
    profitabilityGrowthTitle: "Profitability and growth decisions",
    profitabilityGrowthDesc: "Measurement and modeling systems connecting channel, cohort, and payback economics for capital allocation.",
    profitabilityGrowthVia: "Via Decision Intelligence",
    aiOpsTitle: "AI operations and control",
    aiOpsDesc: "Monitoring, QA, evaluation, escalation logic, and operational safeguards for production AI systems.",
    aiOpsVia: "Cross-cutting · Post-launch",
    bridgeLabel: "How to engage",
    bridgeTitle: "From problem to engagement.",
    bridgeDesc: "Every solution is delivered through the service path that fits the workflow, data foundation, decision layer, or readiness question. Scope, timing, and investment are confirmed after fit review; deliverables, owners, controls, and acceptance criteria are defined before delivery.",
    bridgeCta: "Explore production AI systems",
    ctaHeadline: "Bring the costly workflow, not a preferred tool.",
    ctaDesc: "We will map the operating boundary, data, authority, production risk, and first credible change worth scoping.",
  },
  solutions: {
    aiAgents: {
      label: "Solution",
      heroTitle: "Move multi-step work out of inboxes without giving AI open-ended authority.",
      hubDesc: "Redesign repetitive operating work around bounded automation, explicit actions, exception routing, and human authority.",
      heroSubtitle: "Move a recurring workflow from inboxes, spreadsheets, and copy-paste work into a controlled system that classifies, retrieves, drafts, updates approved tools, and escalates when risk or uncertainty crosses a defined boundary.",
      problemLabel: "The problem",
      problemTitle: "The queue grows. The operating rules stay trapped in people’s heads.",
      problemP1: "Teams classify requests, gather context, copy data between systems, draft a next step, and route exceptions by hand. Delay and inconsistency compound as volume grows, while simple scripts fail when the path branches or the inputs are incomplete.",
      problemP2: "Vert AI first defines the system boundary: which triggers enter, which sources may be used, which actions are permitted, what confidence is acceptable, and which cases require a person.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Undefined authority to read, write, or act|No reliable route for uncertain or high-impact cases|Weak source data or missing system permissions|No evaluation set for common and edge cases|No audit trail, rollback, or accountable workflow owner",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Trigger, state, and system-boundary design|Rules-and-model orchestration for bounded tasks|Approved data retrieval and tool integrations|Classification, drafting, routing, and permitted actions|Confidence thresholds and exception queues|Human approval and override interfaces|Evaluation sets, audit logs, and rollback paths|Usage, quality, latency, failure, and value monitoring",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Order and inventory exception handling|Vendor, finance, and operations request triage|Product-data enrichment with approval|Returns and service-case routing|Document intake and evidence gathering|Multi-location task coordination|Recurring reconciliation and follow-up",
      humanLabel: "Human oversight",
      humanTitle: "Humans stay in the loop.",
      humanDesc: "The operating owner defines where a person approves, overrides, investigates, or stops the workflow. Evaluation measures routing, action accuracy, exception quality, review load, and business impact—not whether the agent looks convincing in a demo.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Agentic AI & Workflow Automation.",
      engagementDesc: "Use this path when one recurring process has a visible cost, an accountable owner, usable inputs, and a defensible review boundary. Start with the AI Verification Review when leadership first needs evidence for a funding, launch, pause, or scale decision.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Review the production workflow engagement",
      ctaHeadline: "Bring the queue, handoffs, and exceptions.",
      ctaDesc: "We will test whether bounded automation can improve the workflow without giving the system authority it should not have.",
    },
    documentIntelligence: {
      label: "Solution",
      heroTitle: "Turn document-heavy work into validated, traceable action.",
      hubDesc: "Extraction, classification, and structured output from contracts, invoices, RFPs, and high-friction document workflows.",
      heroSubtitle: "Turn contracts, invoices, forms, product files, claims, and operational documents into validated structured inputs—with traceability to the source and a review path for uncertain fields.",
      problemLabel: "The problem",
      problemTitle: "The document is not the workflow. Validation and action are.",
      problemP1: "Teams open files, identify the document type, find the relevant fields, reconcile them against policies or systems, correct exceptions, and post approved data downstream. Extraction alone does not remove that operating work.",
      problemP2: "Vert AI designs the full boundary from intake to approved output: supported formats, field definitions, source evidence, validation rules, human review, downstream writes, and exception ownership.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Format and language variation outside the happy path|Fields that require business context, not OCR|No source evidence or field-level confidence|No reconciliation against master data or policy|No exception queue, correction feedback, or safe downstream write",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Document intake, classification, and version handling|Field and clause extraction with source evidence|Entity matching and business-rule validation|Confidence thresholds and review queues|Approved correction and feedback capture|Document-to-system integrations with audit logs|Evaluation sets spanning formats and edge cases|Monitoring for field quality, exceptions, and drift",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Procurement contract review|Accounts payable automation|Legal document analysis|Insurance underwriting document processing|Compliance document classification|Proposal intake and scoring|Regulatory filing extraction",
      humanLabel: "Human oversight",
      humanTitle: "Humans validate what matters.",
      humanDesc: "People approve low-confidence, contradictory, high-value, or policy-sensitive fields before the system acts. Evaluation is field- and workflow-specific: extraction quality, validation accuracy, exception recall, reviewer effort, and downstream error rate.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Production AI Systems.",
      engagementDesc: "Use Production AI Systems when the document path and downstream action are clear. Use Data Platforms for AI when entity definitions, master data, or source quality must be fixed first.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Review the production workflow engagement",
      ctaHeadline: "Show us the documents, corrections, and downstream action.",
      ctaDesc: "We will map the full workflow and identify what must remain under human authority.",
    },
    knowledgeSystems: {
      label: "Solution",
      heroTitle: "Give teams reliable answers from approved knowledge—with evidence.",
      hubDesc: "Governed answers and workflow support grounded in approved policies, procedures, product data, and operating knowledge.",
      heroSubtitle: "Give teams a governed way to find and apply approved knowledge, with permission-aware retrieval, citations, abstention, feedback, and a clear owner for every source collection.",
      problemLabel: "The problem",
      problemTitle: "The answer exists, but its authority and freshness are unclear.",
      problemP1: "Policies, procedures, product facts, and commercial knowledge are scattered across drives, wikis, tickets, and legacy tools. Search may find a document without establishing whether it is current, permitted, or sufficient for the user’s decision.",
      problemP2: "Vert AI defines the knowledge boundary: approved sources, permissions, version and freshness rules, retrieval and citation behaviour, abstention, feedback, and the workflow action that follows the answer.",
      gapsLabel: "Why generic search falls short",
      gaps: "Stale or conflicting sources with no accountable owner|Permission leakage across teams or roles|Answers without citations or source-level traceability|Retrieval that misses the right passage or invents a bridge|No abstention, feedback, or test set for representative questions",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Source inventory, ownership, and permission model|Ingestion, version, freshness, and deletion workflows|Hybrid retrieval tuned to representative questions|Citation, confidence, and abstention behaviour|Role-aware interfaces inside the operating workflow|Feedback, correction, and knowledge-gap queues|Evaluation for retrieval, groundedness, and answer usefulness|Monitoring for source health, failures, and adoption",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "HR policy and benefits questions|Technical support knowledge retrieval|Sales enablement and product information|Regulatory compliance knowledge access|Onboarding and training knowledge delivery|Operations procedure lookup|Cross-department knowledge sharing",
      humanLabel: "Human oversight",
      humanTitle: "People retain authority over knowledge and action.",
      humanDesc: "Content owners approve sources and corrections. Users can inspect citations and escalate an answer. High-impact actions stay outside the assistant unless a separate, controlled workflow explicitly grants that authority.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Production AI Systems.",
      engagementDesc: "Use this path when the knowledge task, source owners, users, permissions, and next action are clear. Start with Data Platforms for AI when source quality, entity definitions, or access rules are the dominant risk.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Review the production workflow engagement",
      ctaHeadline: "Bring the questions, approved sources, and decision that follows.",
      ctaDesc: "We will test whether retrieval is the right mechanism and what evidence users need to trust it.",
    },
    customerSupport: {
      label: "Solution",
      heroTitle: "Reduce service effort without losing policy or escalation control.",
      hubDesc: "Triage, agent assistance, bounded resolution, and escalation designed around service quality and customer risk.",
      heroSubtitle: "Redesign support intake, classification, context gathering, response drafting, permitted resolution, and human handoff as one measurable service workflow.",
      problemLabel: "The problem",
      problemTitle: "Volume rises while context and judgment stay manual.",
      problemP1: "Agents classify intent, search for account and policy context, draft responses, choose an action, and decide when to escalate. A chatbot that answers questions but cannot respect account state, policy, permissions, or customer impact adds another queue instead of removing work.",
      problemP2: "Vert AI defines which requests may be assisted or resolved, which data may be accessed, which actions are permitted, and the conditions that force a handoff to the right person.",
      gapsLabel: "Why generic chatbots fall short",
      gaps: "Weak intent and priority labels|Knowledge disconnected from account and order context|No permitted-action model for refunds, changes, or exceptions|Handoffs that lose context or arrive too late|No evaluation for resolution quality, policy compliance, or customer harm",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Intake, intent, urgency, and routing models|Account, order, policy, and knowledge retrieval|Agent-assist drafts with source context|Bounded resolution actions with approval rules|Confidence, risk, and customer-impact thresholds|Context-preserving human handoff and override|Evaluation for routing, response, action, and policy quality|Monitoring for containment, rework, escalation, and service outcomes",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Tier-1 support automation|Order status and tracking inquiries|Returns and exchange processing|Account and billing questions|Product information requests|Service scheduling and coordination|Complaint classification and routing",
      humanLabel: "Human oversight",
      humanTitle: "Agents stay in control.",
      humanDesc: "Agents remain responsible for sensitive, ambiguous, high-value, or relationship-critical cases. They can inspect context, edit or reject suggestions, override actions, and feed corrections back into evaluation.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Production AI Systems.",
      engagementDesc: "Use this path when a support queue has sufficient volume, an accountable service owner, accessible context, and clear human authority. The AI Verification Review or Data Platforms for AI may come first when the decision evidence, labels, policies, or integrations are unreliable.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Review the production workflow engagement",
      ctaHeadline: "Bring the queue, service rules, and hardest handoffs.",
      ctaDesc: "We will define where assistance helps, where automation is safe, and where a person must remain in control.",
    },
    predictiveModels: {
      label: "Solution",
      heroTitle: "Turn forecasts and scores into governed recurring decisions.",
      hubDesc: "Forecasting, scoring, and prioritization embedded in the decision workflow that can act on the signal.",
      heroSubtitle: "Move forecasting, scoring, and prioritization out of notebooks and dashboards into a governed decision workflow with thresholds, explanation, human judgment, outcome capture, and monitoring.",
      problemLabel: "The problem",
      problemTitle: "A prediction has no value until the operating team can act on it.",
      problemP1: "Dashboards describe the past and notebooks produce scores, but teams still decide in spreadsheets and meetings. The missing layer is the policy around the model: who receives the signal, what threshold changes an action, which constraints apply, and how the outcome returns as feedback.",
      problemP2: "Vert AI frames the decision before the model, then builds the data, prediction, threshold, review, action, and learning loop as one system.",
      gapsLabel: "Why dashboards fall short",
      gaps: "No agreed decision, action, or accountable owner|Historical labels that do not represent the real outcome|Leakage, bias, drift, or unstable business definitions|No threshold policy or capacity constraint|No outcome capture to test whether the decision improved",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Decision definition and baseline measurement|Training data, labels, leakage, and quality controls|Forecasting, scoring, ranking, or classification models|Threshold and capacity policy design|Explanation and human decision interface|Batch or real-time workflow integration|Outcome and override feedback capture|Performance, drift, adoption, and value monitoring",
      workflowsLabel: "Typical decisions",
      workflowsTitle: "Decisions we improve.",
      workflows: "Which customers are most likely to churn|Where to allocate sales effort|What inventory levels to maintain|Which leads to prioritize|How to segment customers by value|Where to adjust pricing|Which marketing cohorts to invest in",
      humanLabel: "Decision support, not replacement",
      humanTitle: "Models inform. Humans decide.",
      humanDesc: "The business owner sets the policy and retains authority over exceptions, constrained resources, and high-impact decisions. Evaluation covers model quality and decision quality: calibration, ranking lift, threshold trade-offs, overrides, adoption, and the operating outcome.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Decision Intelligence.",
      engagementDesc: "Use this path when the recurring decision, available action, outcome label, owner, and baseline are sufficiently clear. Start with the AI Verification Review or Data Platforms for AI when they are not.",
      engagementHref: "/services/decision-intelligence",
      engagementCta: "Review the decision-systems engagement",
      ctaHeadline: "Name the decision, action, and measurable consequence.",
      ctaDesc: "We will test whether a predictive system can improve the operating decision—not merely produce a more sophisticated score.",
    },
  },
  seo: {
    home: {
      title: "Fix Costly Commerce Workflows with Production AI | Vert AI",
      description: "Vert AI fixes costly commerce workflows by redesigning the work and building the production AI, reliable data, controls, and measurement needed to operate it.",
      keywords: "production AI workflow systems, commerce operations AI, AI implementation Canada, workflow automation, decision systems",
    },
    services: {
      title: "Production AI Engagements for Commerce Operations | Vert AI",
      description: "Start with one evidence-backed AI decision review, then choose the supported path: production AI, agentic workflow automation, data, or decision intelligence.",
      keywords: "AI verification review Canada, production AI systems, agentic workflow automation, data platforms for AI, decision intelligence",
    },
    dataPlatforms: {
      title: "Data Platforms for AI | Vert AI",
      description: "Build the pipelines, definitions, quality controls, permissions, lineage, and activation layer required by a reliable production AI system.",
      keywords: "data foundations for AI, data quality, semantic layer, data governance, production AI data engineering Canada",
    },
    solutions: {
      title: "AI Solutions for Costly Commerce Workflows | Vert AI",
      description: "Review five controlled solution patterns for manual handoffs, document work, scattered knowledge, service queues, and recurring operating decisions.",
      keywords: "production AI solutions, workflow automation, document intelligence, enterprise retrieval, AI customer support, predictive models",
    },
    aiAgents: {
      title: "Bounded AI Agents & Workflow Automation | Vert AI",
      description: "Redesign a recurring workflow with bounded AI agents, explicit routing, tool permissions, fallback paths, human authority, and production evaluation.",
      keywords: "bounded AI agents, AI workflow automation, human review, agent evaluation, production AI",
    },
    documentIntelligence: {
      title: "Document Intelligence & Exception Workflows | Vert AI",
      description: "Turn documents into validated structured data with extraction, confidence thresholds, exception queues, human review, and traceable source records.",
      keywords: "document intelligence, document extraction, exception workflow, human validation, intelligent document processing",
    },
    knowledgeSystems: {
      title: "Enterprise Knowledge Retrieval with Citations | Vert AI",
      description: "Build governed retrieval across policies, procedures, product data, and commercial documents with access controls, source citations, evaluation, and abstention.",
      keywords: "enterprise knowledge retrieval, RAG, source citations, access-controlled search, retrieval evaluation",
    },
    customerSupport: {
      title: "AI Customer Support with Human Handoffs | Vert AI",
      description: "Design support triage, agent assistance, and bounded resolution workflows with grounded answers, confidence thresholds, escalation, and quality review.",
      keywords: "AI customer support, agent assist, support triage, human handoff, grounded responses",
    },
    predictiveModels: {
      title: "Predictive Models Embedded in Decisions | Vert AI",
      description: "Build forecasting, scoring, and prioritization systems with decision thresholds, human authority, monitoring, and feedback inside the operating workflow.",
      keywords: "predictive models, applied machine learning, demand forecasting, decision thresholds, model monitoring",
    },
    industries: {
      title: "Production AI for Commerce Operations | Vert AI",
      description: "Explore workflow, data, control, and readiness realities for retail and ecommerce, consumer goods, and franchise or multi-location operations.",
      keywords: "retail AI Canada, ecommerce operations AI, consumer goods AI, franchise AI, commerce workflow automation",
    },
    work: {
      title: "Real Anonymized Production AI Work | Vert AI",
      description: "Review four real anonymized engagements, the operating problem, intervention, production controls, measurement frame, and limits of the published evidence.",
      keywords: "production AI work, anonymized AI engagements, AI delivery evidence, production controls",
    },
    aiReadinessScorecard: {
      title: "Directional AI Readiness Scorecard | Vert AI",
      description: "Use an ungated, equally weighted heuristic to assess a workflow, decision, or data problem and identify what needs attention before a production build.",
      keywords: "AI readiness scorecard, production AI readiness, workflow assessment, AI opportunity mapping",
    },
    insights: {
      title: "AI Decision Guides for Operators and Buying Teams | Vert AI",
      description: "Sixteen sourced guides for evaluating workflow value, readiness, data, controls, economics, governance, vendors, and production risk.",
      keywords: "production AI guides, AI workflow selection, AI governance, AI evaluation, AI ROI, data readiness",
    },
    about: {
      title: "About Vert AI | Senior Accountability for Production AI",
      description: "How Vert AI fixes costly workflows with founder-led scoping, senior technical decisions, vetted specialists, phased evidence, and clear fit limits.",
      keywords: "Vert AI, Guillaume Belisle, founder-led AI implementation, production AI Canada",
    },
    contact: {
      title: "Discuss a Costly Commerce Workflow | Vert AI",
      description: "Bring a costly workflow or recurring decision, its owner, current systems, and visible operating cost. Vert AI will recommend a credible next step.",
      keywords: "contact Vert AI, discuss AI workflow, production AI consultation Canada",
    },
    privacy: {
      title: "Website Privacy | Vert AI",
      description: "How Vert AI handles information submitted through the website, contact form, readiness scorecard, analytics layer, and external booking links.",
    },
  notFound: {
      title: "Page Not Found | Vert AI",
      description: "The page you are looking for does not exist or has moved.",
    },
    unsubscribe: {
      title: "Unsubscribe | Vert AI",
      description: "Manage your email preferences.",
    },
  },
};
